import { useCallback, useEffect, useState } from 'react';
import FamilyBlock from '../components/FamilyBlock';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  User,
  Phone,
  Mail,
  Calendar,
  Shield,
  FileText,
  Edit,
  Save,
  X,
  ChevronDown,
  ChevronRight,
  Gift,
  HeartPulse,
} from 'lucide-react';
import { format } from 'date-fns';
import { supabase } from '../services/supabase';
import { leerFichaMedica, borrarFichaMedica } from '../services/fichaMedica';
import type { Database } from '../types/database.types';
import FichaAlumno from '../components/FichaAlumno';
import RegalarProductoModal from '../components/RegalarProductoModal';
import { listaEnCastellano } from '../lib/leerVarias';
import { CATEGORY_LABELS } from '../lib/categorias';
import RevisarCategoriasModal, {
  leerPreview,
  filaDelAlumno,
  type RederivFila,
} from '../components/RevisarCategorias';

// Perfil completo desde el generado — incluye `club/team/division`
// (migración 024 / 047) que el `Profile` manual omitía. La 5.7a retira el
// import del manual; el residuo `as any` para esos campos desaparece.
//
// ⚠️ `medical_info` SE EXCLUYE. El perfil se lee de la VISTA y el paso 3 del
// punto 5 (§71) la quita de ahí; como este tipo venía de la TABLA, habría
// seguido compilando mientras el valor llegaba `undefined` y la ficha de un
// alumno se pintaba como «Sin información registrada» sin ningún error.
// Excluirla obliga a pedirla por `get_medical_info`, que deja fila.
type ProfileFull = Omit<Database['public']['Tables']['profiles']['Row'], 'medical_info'>;

// Tipos del dominio "bonos y créditos" (Fase 5.3). El bloque "evaluaciones"
// (panel inferior) usa el Row generado (Fase 5.6b) — el manual
// `StudentEvaluation` quedó retirado porque divergía del schema real
// (modelo viejo `actitud/esfuerzo/motivacion/overall_score` que el
// migrate 033 sustituyó por `detailed_scores` + scores por categoría).
// El panel ahora muestra solo campos que existen: fecha, monitor,
// `overall_comment` y `attitude_score` cuando es por sesión.
type UserCreditRow = Database['public']['Tables']['user_credits']['Row'];
type SessionRow = Database['public']['Tables']['sessions']['Row'];
type EnrollmentRow = Database['public']['Tables']['enrollments']['Row'];
type FormalEvaluationRow = Database['public']['Tables']['formal_evaluations']['Row'];

// Embed `profiles!monitor_id(full_name)` — supabase-js devuelve la relación
// bajo la key `profiles`. Doble cast vía `unknown` (patrón ADR-0020).
type EvaluationWithMonitor = Pick<
  FormalEvaluationRow,
  'id' | 'period_start' | 'period_end' | 'overall_comment'
> & {
  profiles: { full_name: string } | null;
};

// [248] Lo que le han regalado a este alumno, venga o no en forma de bono. La
// lista de bonos ya marca los regalados, pero no dice QUIÉN los dio — y de una
// matrícula o una camiseta regaladas no había ni rastro en la ficha.
interface RegaloRecibido {
  id: string;
  created_at: string;
  gifted_by: string | null;
  gift_reason: string | null;
  que: string;
}

// Bono cargado con embed del pack (loadAlumno).
type CreditWithPack = UserCreditRow & {
  session_packs: { name: string; sessions_count: number } | null;
};

// Sesiones inscritas con un bono concreto (toggleCreditExpand): enrollment
// + session + grupo / clase especial.
type CreditEnrollmentRow = Pick<EnrollmentRow, 'id' | 'attendance'> & {
  sessions:
    | (Pick<SessionRow, 'date' | 'time_start' | 'time_end'> & {
        groups: { name: string } | null;
        special_classes: { name: string } | null;
      })
    | null;
};

// Historial de sesiones del alumno (loadAlumno → enrollments con embed).
// Mismo shape que CreditEnrollmentRow pero sin `time_end` (no se pide en el select).
type HistorialEnrollmentRow = EnrollmentRow & {
  sessions:
    | (Pick<SessionRow, 'date' | 'time_start'> & {
        groups: { name: string } | null;
        special_classes: { name: string } | null;
      })
    | null;
};

// EL ORDEN DE LO QUE SE VE. Vive aquí, en el componente, y no en la consulta.
// Dos motivos, los dos medidos el 2026-10-22:
//
//  1 · La consulta ordenaba por `enrolled_at`, y esa columna NO SEPARA. Hay
//      alumnos con 12 inscripciones y UN SOLO instante de `enrolled_at` —el
//      alta por suscripción las inserta en bloque, con el mismo `now()`—, y el
//      mayor grupo empatado medido es de 12 filas. Con un empate así, el orden
//      DENTRO del grupo es indefinido y lo que sale es el orden físico: de ahí
//      los tramos ascendentes seguidos que se veían en la ficha. O sea que no
//      era «falta un ORDER BY» ni «ordena por la columna equivocada»: había un
//      ORDER BY y la columna no discriminaba.
//
//  2 · Y si el orden viviera en la consulta, no habría forma de CAZARLO. Esta
//      página se testea con un cliente Supabase real y MSW delante, y MSW
//      devuelve el fixture tal cual: no implementa el `order=` de PostgREST.
//      Un test anclado en la consecuencia —un fixture desordenado que tiene
//      que salir ordenado— solo puede pasar si el orden es una propiedad del
//      componente. Anclarlo en que exista un `.order()` en una línea se rompe
//      con el primer refactor, y encima se rompe en verde.
//
// `enrolled_at` SIGUE en la consulta, pero para otra cosa y conviene no
// confundirlas: allí elige QUÉ 20 filas bajan (las 20 reservas más recientes);
// aquí se decide cómo se pintan. Medido: el alumno con más inscripciones tiene
// 17 y ninguno de los 52 pasa de 20, así que hoy el límite no corta a nadie.
//
// El desempate por `id` no es decorativo: dos inscripciones a la misma fecha y
// hora no pueden quedar en orden indefinido, que es exactamente el defecto que
// esto arregla. Es un uuid, así que el orden entre ellas es arbitrario — pero
// ESTABLE, que es lo que hacía falta. Se usa `id` y no `enrolled_at` porque
// `CreditEnrollmentRow` no lo trae en su `select`.
function porFechaYHora<T extends { id: string; sessions: { date: string; time_start: string } | null }>(
  filas: T[]
): T[] {
  return [...filas].sort((a, b) => {
    const ka = `${a.sessions?.date ?? ''}T${a.sessions?.time_start ?? ''}`;
    const kb = `${b.sessions?.date ?? ''}T${b.sessions?.time_start ?? ''}`;
    if (ka !== kb) return ka < kb ? -1 : 1;
    return a.id.localeCompare(b.id);
  });
}

// Resultado real de `rpc('admin_grant_credits', ...)`. El generador emite
// `Returns: Json` (pierde el shape). El convenio del proyecto para RPCs
// admin/staff es `{ ok, credit_id?, error? }` (mismo patrón que EnrollResult).
type GrantCreditsResult = { ok: true; credit_id: string } | { ok: false; error: string };

const POSITION_LABELS: Record<string, string> = {
  portero: 'Portero',
  defensa: 'Defensa',
  centrocampista: 'Centrocampista',
  delantero: 'Delantero',
  extremo: 'Extremo',
};

const CREDIT_TYPE_LABELS: Record<string, string> = {
  grupal: 'Grupal',
  individual: 'Individual',
  especial: 'Especial',
  gym: 'PF',
};
const CREDIT_TYPE_COLORS: Record<string, string> = {
  grupal: 'badge-gold',
  individual: 'badge-silver',
  especial: 'badge-warning',
  gym: 'badge-success',
};

// Se reexporta desde donde vive ahora (lib/leerVarias.ts): los PR siguientes
// de la serie la necesitan y no van a importar de una pantalla.
export { listaEnCastellano };

// Nombres de los dos bloques que esta serie añade al aviso de arriba. Los de
// la carga principal viven en `loadAlumno`.
const QUE_REGALOS = 'los regalos recibidos';
const QUE_QUIEN = 'quién hizo cada regalo';

export default function AlumnoDetallePage() {
  const { id } = useParams<{ id: string }>();
  const [profile, setProfile] = useState<ProfileFull | null>(null);
  const [credits, setCredits] = useState<CreditWithPack[]>([]);
  const [enrollments, setEnrollments] = useState<HistorialEnrollmentRow[]>([]);
  const [evaluations, setEvaluations] = useState<EvaluationWithMonitor[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState<Partial<ProfileFull>>({});
  // La ficha va aparte de `editData` porque ya no es una columna del tipo del
  // perfil: `fichaBorrador` es lo que hay en el textarea y `fichaGuardada` lo
  // que hay en la base, leído por la vía auditada.
  const [fichaBorrador, setFichaBorrador] = useState('');
  const [fichaGuardada, setFichaGuardada] = useState<string | null>(null);
  const [fichaEstado, setFichaEstado] = useState<'cargando' | 'ok' | 'error'>('cargando');
  const [fichaError, setFichaError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // [AVISO] LA CATEGORÍA DESFASADA RESPECTO AL AÑO.
  //
  // Por qué aquí y no solo en la lista: la categoría sale de `birth_date`, y
  // cuando una fecha se corrige la categoría se queda atrás. Hasta ahora eso no
  // lo veía nadie hasta que a alguien se le ocurría pulsar «Revisar categorías»
  // en Alumnos. El dato lo da el SERVIDOR —la página no recalcula el año, que
  // sería una segunda copia de la tabla de años que ya vive en
  // `categoria_por_anio`—: se le pregunta por el preview y se busca a este
  // alumno dentro.
  const [desfase, setDesfase] = useState<{ fila: RederivFila; aMano: boolean } | null>(null);
  const [desfaseError, setDesfaseError] = useState('');
  const [revisarOpen, setRevisarOpen] = useState(false);
  const [expandedCredit, setExpandedCredit] = useState<string | null>(null);
  const [creditEnrollments, setCreditEnrollments] = useState<Record<string, CreditEnrollmentRow[]>>(
    {}
  );
  const [loadingCredit, setLoadingCredit] = useState<string | null>(null);

  // Regalar sesiones
  const [giftModal, setGiftModal] = useState(false);
  const [giftForm, setGiftForm] = useState({
    sessions: 1,
    credit_type: 'grupal',
    reason: '',
    expiry_date: '',
  });
  const [granting, setGranting] = useState(false);
  const [giftError, setGiftError] = useState('');
  // Regalar un artículo del catálogo (mig 246). Es un flujo distinto del de
  // arriba, no una variante suya: aquél concede sesiones sueltas con la
  // caducidad que le pongas y sin artículo detrás; éste concede algo que el
  // club vende, con su reparto, su talla y su descuento de inventario.
  const [regaloModal, setRegaloModal] = useState(false);
  const [regalos, setRegalos] = useState<RegaloRecibido[]>([]);
  const [quienRegalo, setQuienRegalo] = useState<Record<string, string>>({});
  // Editar la ficha pintaba los datos nuevos y salía del modo edición sin
  // mirar el resultado: el admin veía su cambio aplicado aunque la base lo
  // hubiera rechazado, y volvía al valor viejo en la siguiente recarga.
  const [editError, setEditError] = useState('');
  // Leer y no poder leer no son lo mismo. Antes esta ficha descartaba el
  // `error` de las cuatro consultas, así que un fallo de lectura dejaba el
  // perfil a null y la pantalla afirmaba "Alumno no encontrado": el panel
  // contaba que alguien no existe cuando lo único cierto es que no consiguió
  // mirarlo. Es lo mismo que hacía /pedidos con el 300 del #496.
  const [errorCarga, setErrorCarga] = useState('');
  // Las tres listas van aparte del perfil: que fallen los bonos no es motivo
  // para esconder la ficha entera, pero pintarlas vacías sin decir nada
  // convierte "no pude leerlo" en "no tiene nada".
  const [bloquesFallidos, setBloquesFallidos] = useState<string[]>([]);
  /** Bonos cuyas sesiones no se pudieron leer, por id. Ver `toggleCreditExpand`. */
  const [creditosIlegibles, setCreditosIlegibles] = useState<Record<string, string>>({});
  /** El regalo salió, pero el aviso al alumno no. Ver `grantCredits`. */
  const [avisoPush, setAvisoPush] = useState('');
  const [tab, setTab] = useState<'resumen' | 'ficha'>('resumen');

  const loadAlumno = useCallback(async (userId: string) => {
    setErrorCarga('');
    setBloquesFallidos([]);
    const [
      { data: p, error: errP },
      { data: c, error: errC },
      { data: e, error: errE },
      { data: ev, error: errEv },
    ] = await Promise.all([
      supabase.from('profiles_with_medical_info').select('*').eq('id', userId).single(),
      supabase
        .from('user_credits')
        .select('*, session_packs(name, sessions_count)')
        .eq('user_id', userId)
        .order('purchase_date', { ascending: false }),
      supabase
        .from('enrollments')
        .select('*, sessions(date, time_start, groups(name), special_classes(name))')
        .eq('user_id', userId)
        .order('enrolled_at', { ascending: false })
        .limit(20),
      supabase
        .from('formal_evaluations')
        .select('id, period_start, period_end, overall_comment, profiles!monitor_id(full_name)')
        .eq('student_id', userId)
        .order('period_start', { ascending: false })
        .limit(10),
    ]);
    // `PGRST116` es "cero filas": el alumno de verdad no está, y decirlo es
    // correcto. Cualquier otro error —RLS, red, un embed ambiguo— significa que
    // la consulta no llegó a responder por él, y ahí afirmar que no existe es
    // inventarse una respuesta que nadie dio.
    if (errP && errP.code !== 'PGRST116') {
      setErrorCarga(errP.message || 'Error desconocido');
      setLoading(false);
      return;
    }
    setProfile(p as ProfileFull);
    // La ficha aparte y por la vía auditada: deja fila con via='admin'.
    void (async () => {
      const f = await leerFichaMedica(userId);
      if (f.estado === 'ok') {
        setFichaGuardada(f.ficha);
        setFichaEstado('ok');
      } else {
        setFichaEstado('error');
        setFichaError(f.mensaje);
      }
    })();
    setCredits((c ?? []) as unknown as CreditWithPack[]);
    setEnrollments(porFechaYHora((e ?? []) as unknown as HistorialEnrollmentRow[]));
    setEvaluations((ev ?? []) as unknown as EvaluationWithMonitor[]);
    setBloquesFallidos(
      [
        errC ? 'los bonos' : null,
        errE ? 'el historial de sesiones' : null,
        errEv ? 'las evaluaciones' : null,
      ].filter((v): v is string => v != null)
    );
    setLoading(false);

    // [248] Los regalos van DESPUÉS de pintar la ficha, y a propósito: son lo
    // menos importante de la pantalla y no deben retrasar lo demás.
    //
    // Corrección de lo que decía aquí antes: hablaba de que un `Promise.all`
    // sin catch dejaría `setLoading(false)` sin ejecutar. No ocurre.
    // postgrest-js envuelve la petición en un `.catch` propio y devuelve
    // `{ data: null, error }` mientras `shouldThrowOnError` sea false, así que
    // estas consultas no rechazan nunca. El fallo real era otro y está arriba:
    // el `error` se tiraba.
    await cargarRegalos(userId);
  }, []);

  // ⚠️ El bloque de regalos se pinta con `{regalos.length > 0 && …}`, así que
  //    un fallo de lectura no lo dejaba vacío: lo hacía DESAPARECER. Y este es
  //    el registro de lo que el club ya le ha dado a un alumno; quien entra a
  //    comprobar si ya se le regaló algo lee que no y lo regala otra vez. Es el
  //    mismo fallo que BonosManualesPage (#579), en la otra pantalla donde se
  //    conceden cosas a mano.
  async function cargarRegalos(userId: string) {
    const { data: g, error: errG } = await supabase
      .from('orders')
      .select('id, created_at, items, gifted_by, gift_reason')
      .eq('user_id', userId)
      // `gifted_by` es lo que separa un regalo del club de un canje gratuito
      // de código, que también deja un pedido a 0 €.
      .not('gifted_by', 'is', null)
      .order('created_at', { ascending: false })
      .limit(20);
    if (errG) {
      setBloquesFallidos((prev) => (prev.includes(QUE_REGALOS) ? prev : [...prev, QUE_REGALOS]));
      return;
    }

    const filas = (g ?? []) as unknown as {
      id: string;
      created_at: string;
      items: unknown;
      gifted_by: string | null;
      gift_reason: string | null;
    }[];
    setRegalos(
      filas.map((o) => ({
        id: o.id,
        created_at: o.created_at,
        gifted_by: o.gifted_by,
        gift_reason: o.gift_reason,
        // El nombre viaja sellado en el pedido, así que no hace falta volver al
        // catálogo: lo que se regaló se lee tal y como era ese día.
        que:
          (Array.isArray(o.items) ? (o.items as { name?: string; quantity?: number }[]) : [])
            .map((i) => (i.quantity && i.quantity > 1 ? `${i.quantity}× ${i.name}` : i.name))
            .filter(Boolean)
            .join(', ') || 'Sin detalle',
      }))
    );

    // Quién los regaló, en un mapa aparte: `orders` tiene tres claves ajenas a
    // `profiles` y un embed sin desambiguar devuelve un 300 (#496).
    const ids = [...new Set(filas.map((o) => o.gifted_by).filter((v): v is string => !!v))];
    if (ids.length > 0) {
      // Quién lo regaló es la pregunta de auditoría de este bloque, así que su
      // fallo también se cuenta. Sin el mapa, cada fila cae a «Alguien del
      // club», que no es falso pero tampoco es la respuesta.
      const { data: quienes, error: errQ } = await supabase
        .from('profiles')
        .select('id, full_name')
        .in('id', ids);
      if (errQ) {
        setBloquesFallidos((prev) => (prev.includes(QUE_QUIEN) ? prev : [...prev, QUE_QUIEN]));
      }
      setQuienRegalo(
        Object.fromEntries(
          ((quienes ?? []) as { id: string; full_name: string | null }[]).map((q) => [
            q.id,
            q.full_name ?? 'Alguien del club',
          ])
        )
      );
    }
  }

  // Va DESPUÉS de las dos funciones a propósito: `loadAlumno` es un `const`, y
  // el array de dependencias se evalúa durante el render. Declarado antes, la
  // ficha reventaba con "Cannot access 'loadAlumno' before initialization".
  useEffect(() => {
    if (id) loadAlumno(id);
  }, [id, loadAlumno]);

  // Un fallo de lectura se DICE. «No se pudo comprobar» y «está al día» son
  // cosas distintas, y confundirlas es lo que hace que nadie vuelva a mirar.
  const mirarDesfase = useCallback(async (userId: string) => {
    setDesfaseError('');
    const r = await leerPreview();
    if (r.estado === 'error') {
      setDesfase(null);
      setDesfaseError(r.mensaje);
      return;
    }
    setDesfase(filaDelAlumno(r.preview, userId));
  }, []);

  useEffect(() => {
    if (id) void mirarDesfase(id);
  }, [id, mirarDesfase]);

  async function toggleCreditExpand(creditId: string) {
    if (expandedCredit === creditId) {
      setExpandedCredit(null);
      return;
    }
    setExpandedCredit(creditId);
    if (creditEnrollments[creditId]) return;
    setLoadingCredit(creditId);
    // «Sin sesiones registradas con este bono» es el dato con el que se decide
    // si se devuelve o se reasigna. Un fallo decía lo mismo que un bono sin
    // usar, así que se guarda el error POR BONO y la fila lo dice.
    const { data, error: errE } = await supabase
      .from('enrollments')
      .select('*, sessions(date, time_start, time_end, groups(name), special_classes(name))')
      .eq('credit_id', creditId)
      .order('enrolled_at', { ascending: false });
    setLoadingCredit(null);
    if (errE) {
      setCreditosIlegibles((prev) => ({
        ...prev,
        [creditId]: errE.message || 'Error desconocido',
      }));
      return;
    }
    // Se reintentó y esta vez sí: fuera la marca de ilegible.
    setCreditosIlegibles((prev) => {
      if (!(creditId in prev)) return prev;
      const resto = { ...prev };
      delete resto[creditId];
      return resto;
    });
    // Mismo defecto y mismo arreglo: esta lista ordenaba igual por `enrolled_at`.
    const rows = porFechaYHora((data ?? []) as unknown as CreditEnrollmentRow[]);
    setCreditEnrollments((prev) => ({ ...prev, [creditId]: rows }));
  }

  async function saveEdits() {
    if (!id || !editData) return;
    setSaving(true);
    setEditError('');
    // `medical_info` no está en `editData` (fuera del tipo), así que entra
    // aquí de forma explícita. Se escribe en la TABLA, que sí tiene grant de
    // UPDATE por columna; lo que no hay es grant de SELECT, y por eso leerla
    // pasa por la RPC.
    const ficha = fichaBorrador.trim();
    const habia = (fichaGuardada ?? '').trim() !== '';
    // Dejar la ficha en blanco NO es guardar: es borrar un dato de salud de
    // otra persona. Se pregunta con su nombre delante, y si dice que sí el
    // borrado va por la RPC auditada (mig 283) — nunca en el UPDATE, que la
    // base rechaza con 42501 justo para que un cliente desactualizado no la
    // borre sin que nadie se entere.
    const borraLaFicha = ficha === '' && habia;
    if (borraLaFicha) {
      const ok = window.confirm(
        `Vas a BORRAR la información médica de ${profile?.full_name ?? 'este alumno'}.\n\n` +
          'No es un guardado: el dato se borra, sus entrenadores dejarán de verlo, y ' +
          'queda registrado que lo has borrado tú.\n\n¿Seguro?'
      );
      if (!ok) {
        // No se guarda NADA. El formulario se queda como está para que corrija
        // la ficha en vez de descubrir después que el resto sí se guardó.
        setSaving(false);
        setEditError('No se ha guardado nada: cancelaste el borrado de la ficha médica.');
        return;
      }
    }

    // `medical_info` solo viaja en el UPDATE cuando tiene contenido.
    const campos = borraLaFicha || ficha === '' ? editData : { ...editData, medical_info: ficha };
    const { error } = await supabase.from('profiles').update(campos).eq('id', id);
    if (error) {
      setSaving(false);
      // Se queda en modo edición: lo escrito sigue en pantalla para reintentar.
      setEditError(`No se pudo guardar la ficha: ${error.message}`);
      return;
    }

    if (borraLaFicha) {
      const res = await borrarFichaMedica(id);
      if (res.estado === 'error') {
        setSaving(false);
        setEditError(`Se guardó el resto, pero no se pudo borrar la ficha médica: ${res.mensaje}`);
        return;
      }
    }

    setSaving(false);
    setProfile((prev) => (prev ? { ...prev, ...editData } : prev));
    setFichaGuardada(borraLaFicha || ficha === '' ? null : ficha);
    setEditing(false);
  }

  async function handleGrantCredits() {
    if (!id) return;
    if (giftForm.sessions < 1) {
      setGiftError('Mínimo 1 sesión');
      return;
    }
    setGranting(true);
    setGiftError('');
    // Args están tipados en el archivo generado; el shape de Returns (Json
    // genérico) lo casteamos al convenio del proyecto.
    type GrantArgs = Database['public']['Functions']['admin_grant_credits']['Args'];
    const args: GrantArgs = {
      p_student_id: id,
      p_sessions: giftForm.sessions,
      p_credit_type: giftForm.credit_type,
      p_reason: giftForm.reason || undefined,
      p_expiry_date: giftForm.expiry_date
        ? new Date(giftForm.expiry_date).toISOString()
        : undefined,
    };
    const { data, error } = await supabase.rpc('admin_grant_credits', args);
    setGranting(false);
    const result = data as GrantCreditsResult | null;
    if (error || !result?.ok) {
      const errorMessage = result && !result.ok ? result.error : error?.message;
      setGiftError(errorMessage ?? 'Error al regalar sesiones');
      return;
    }
    setGiftModal(false);
    setGiftForm({ sessions: 1, credit_type: 'grupal', reason: '', expiry_date: '' });

    // Enviar push al alumno.
    //
    // ⚠️ El bono YA está concedido cuando llegamos aquí, así que nada de esto
    //    puede deshacerlo ni debe parecer un fallo de la concesión. Lo que sí
    //    hace falta es no callarse: si no se puede leer el token, el aviso no
    //    sale y hasta ahora nadie se enteraba —ni dirección, que da por hecho
    //    que el alumno ya lo sabe, ni el alumno, que no recibe nada—. Se dice,
    //    y se dice que el regalo está hecho.
    supabase
      .from('profiles')
      .select('push_token')
      .eq('id', id)
      .single()
      .then(({ data: prof, error: errTok }) => {
        if (errTok) {
          setAvisoPush(
            'El bono está concedido, pero no se ha podido comprobar si al alumno le llega el aviso. Díselo tú.'
          );
          return;
        }
        const pushToken = (prof as { push_token: string | null } | null)?.push_token;
        if (!pushToken) {
          setAvisoPush(
            'El bono está concedido. Ese alumno no tiene la app con avisos activados, así que no le llegará ninguna notificación.'
          );
          return;
        }
        supabase.functions
          .invoke('gym-send-push', {
            body: {
              user_id: id,
              title: '¡Has recibido un bono regalo!',
              body: `Te han concedido ${giftForm.sessions} sesión(es) ${giftForm.credit_type}.${giftForm.reason ? ' Motivo: ' + giftForm.reason : ''}`,
              type: 'gift',
              data: { credit_id: result.credit_id },
            },
          })
          .catch(() =>
            setAvisoPush(
              'El bono está concedido, pero el aviso no ha salido. Díselo tú o pide que se reenvíe.'
            )
          );
      });

    loadAlumno(id);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-gold-primary/30 border-t-gold-primary rounded-full animate-spin" />
      </div>
    );
  }

  // La lectura falló: no sabemos si el alumno está o no, y la ficha lo dice
  // en esos términos en vez de dar por hecho lo segundo. Con reintentar,
  // porque el motivo más probable —red— se arregla volviéndolo a pedir.
  if (errorCarga) {
    return (
      <div className="space-y-3" data-testid="alumno-error-carga">
        <p className="text-error">No se ha podido cargar la ficha del alumno.</p>
        <p className="text-text-secondary text-sm">{errorCarga}</p>
        <button
          type="button"
          onClick={() => id && loadAlumno(id)}
          data-testid="alumno-reintentar"
          className="px-4 py-2 rounded bg-gold-primary/20 text-gold-primary text-sm hover:bg-gold-primary/30 transition-colors"
        >
          Reintentar
        </button>
      </div>
    );
  }

  if (!profile) {
    return <p className="text-error">Alumno no encontrado</p>;
  }

  const totalSesionesAsistidas = enrollments.filter((e) => e.attendance === true).length;
  const creditosActivos = credits.filter(
    (c) => c.active && (!c.expiry_date || new Date(c.expiry_date) > new Date())
  );
  const sesionesRestantes = creditosActivos.reduce((sum, c) => sum + c.sessions_remaining, 0);

  return (
    <div className="space-y-6 max-w-5xl">
      {/* El perfil se leyó, pero alguna lista no. Se avisa arriba en vez de
          pintarla vacía: un bloque en blanco se lee como "no tiene", y aquí lo
          único cierto es que no se pudo consultar. */}
      {avisoPush && (
        <p
          className="text-warning text-sm bg-warning/10 rounded p-2"
          data-testid="aviso-push-regalo"
        >
          {avisoPush}
        </p>
      )}

      {bloquesFallidos.length > 0 && (
        <p
          className="text-error text-sm bg-error/10 rounded p-2"
          data-testid="alumno-bloques-fallidos"
        >
          No se han podido cargar {listaEnCastellano(bloquesFallidos)}. Lo que se ve de{' '}
          {bloquesFallidos.length === 1 ? 'ese apartado' : 'esos apartados'} puede estar incompleto.
        </p>
      )}

      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/alumnos" className="text-text-secondary hover:text-white transition-colors">
          <ArrowLeft size={20} />
        </Link>
        <div className="flex-1">
          <h2 className="font-oswald text-2xl uppercase tracking-wider text-white">
            {profile.full_name}
          </h2>
          {/* Con la 300, un alumno sin buzón propio tiene `email` a NULL y
              aquí quedaba un hueco en blanco bajo el nombre, que se lee como
              página rota. «sin correo propio» es el dato: se contacta con su
              tutor, que sale más abajo en la ficha. */}
          <p className="text-text-secondary text-sm">
            {profile.email ?? <span className="italic">sin correo propio</span>}
          </p>
        </div>
        {!editing ? (
          <button
            onClick={() => {
              setEditing(true);
              setEditData({
                full_name: profile.full_name,
                phone: profile.phone ?? '',
                position: profile.position ?? null,
                club: profile.club ?? '',
                team: profile.team ?? '',
                division: profile.division ?? '',
                parent_email: profile.parent_email ?? '',
                // ⚠️ `category` FALTABA AQUÍ, y no era cosmético: el <select> lee
                // `editData.category`, así que al entrar en edición mostraba
                // «Sin categoría» aunque el alumno tuviera una. Quien la viera
                // vacía y eligiera la correcta «para arreglarla» estaría
                // haciendo un cambio MANUAL: queda en audit_log con
                // `fuente = manual` y deja al chaval inmune al botón de repaso
                // PARA SIEMPRE, por un valor que ya era correcto.
                category: profile.category ?? null,
              });
              setFichaBorrador(fichaGuardada ?? '');
            }}
            className="btn-outline flex items-center gap-2"
          >
            <Edit size={16} /> Editar
          </button>
        ) : (
          <div className="flex flex-col items-end gap-2">
            {editError && (
              <p className="text-error text-sm" data-testid="alumno-edit-error">
                {editError}
              </p>
            )}
            <div className="flex gap-2">
              <button
                onClick={() => setEditing(false)}
                className="btn-ghost flex items-center gap-2"
              >
                <X size={16} /> Cancelar
              </button>
              <button
                onClick={saveEdits}
                disabled={saving}
                className="btn-gold flex items-center gap-2"
              >
                <Save size={16} /> {saving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Tabs Resumen / Ficha completa (A3) */}
      <div className="flex gap-2 border-b border-bg-border" role="tablist">
        <button
          role="tab"
          aria-selected={tab === 'resumen'}
          data-testid="tab-resumen"
          onClick={() => setTab('resumen')}
          className={`px-4 py-2 text-sm font-oswald -mb-px border-b-2 ${
            tab === 'resumen'
              ? 'border-gold-primary text-gold-primary'
              : 'border-transparent text-text-secondary'
          }`}
        >
          Resumen
        </button>
        <button
          role="tab"
          aria-selected={tab === 'ficha'}
          data-testid="tab-ficha"
          onClick={() => setTab('ficha')}
          className={`px-4 py-2 text-sm font-oswald -mb-px border-b-2 ${
            tab === 'ficha'
              ? 'border-gold-primary text-gold-primary'
              : 'border-transparent text-text-secondary'
          }`}
        >
          Ficha completa
        </button>
      </div>

      {tab === 'ficha' && <FichaAlumno studentId={profile.id} />}

      {tab === 'resumen' && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Datos personales */}
            <div className="card col-span-2 space-y-4">
              <h3 className="font-oswald text-sm uppercase tracking-wider text-white">
                Datos personales
              </h3>

              <div className="grid grid-cols-2 gap-4">
                <Field label="Nombre completo" icon={<User size={14} />}>
                  {editing ? (
                    <input
                      className="input text-sm"
                      value={editData.full_name ?? ''}
                      onChange={(e) => setEditData((d) => ({ ...d, full_name: e.target.value }))}
                    />
                  ) : (
                    <span>{profile.full_name}</span>
                  )}
                </Field>
                <Field label="Teléfono" icon={<Phone size={14} />}>
                  {editing ? (
                    <input
                      className="input text-sm"
                      value={editData.phone ?? ''}
                      onChange={(e) => setEditData((d) => ({ ...d, phone: e.target.value }))}
                    />
                  ) : (
                    <span>{profile.phone ?? '—'}</span>
                  )}
                </Field>
                <Field label="Email" icon={<Mail size={14} />}>
                  {/* «—» se lee como «no lo sabemos». Aquí sí lo sabemos. */}
                  <span>{profile.email ?? 'sin correo propio'}</span>
                </Field>
                <Field label="Fecha de nacimiento" icon={<Calendar size={14} />}>
                  <span>
                    {profile.birth_date ? format(new Date(profile.birth_date), 'dd/MM/yyyy') : '—'}
                  </span>
                </Field>
                {/* [343] LA CATEGORÍA DE VERTEX, corregible a mano. Va aquí, junto a
                    la fecha de nacimiento de la que sale, y NO en el bloque de
                    «Club / Equipo / División», que es el club de FUERA en texto
                    libre. Si se cambia a mano queda constancia en audit_log y el
                    botón de re-derivación ya no propondrá deshacerlo. */}
                <Field label="Categoría">
                  {editing ? (
                    <select
                      className="input text-sm"
                      data-testid="ficha-categoria"
                      value={editData.category ?? ''}
                      onChange={(e) =>
                        setEditData((d) => ({ ...d, category: e.target.value || null }))
                      }
                    >
                      <option value="">Sin categoría</option>
                      {Object.entries(CATEGORY_LABELS).map(([v, l]) => (
                        <option key={v} value={v}>
                          {l}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span>
                      {profile.category ? (CATEGORY_LABELS[profile.category] ?? profile.category) : '—'}
                    </span>
                  )}
                </Field>
                {/* [AVISO] Lo que el servidor dice de ESTE alumno. No hay botón
                    de «aplicar solo a él»: `admin_rederivar_categorias` recibe
                    `(p_aplicar, p_incluir_corregidas)` y nada más. Así que esto
                    abre el MISMO repaso, con su lista delante, y quien mira ve
                    a quién va a tocar antes de tocarlo. */}
                {desfaseError ? (
                  <Field label="">
                    <span className="text-error text-xs" data-testid="ficha-desfase-error">
                      No se pudo comprobar si su categoría está al día: {desfaseError}
                    </span>
                  </Field>
                ) : desfase ? (
                  <Field label="">
                    <div className="space-y-1" data-testid="ficha-desfase">
                      <p className="text-warning text-xs">
                        Su año ({desfase.fila.anio}) da{' '}
                        <strong>
                          {CATEGORY_LABELS[desfase.fila.categoria_nueva] ??
                            desfase.fila.categoria_nueva}
                        </strong>{' '}
                        y tiene{' '}
                        <strong>
                          {desfase.fila.categoria_actual
                            ? (CATEGORY_LABELS[desfase.fila.categoria_actual] ??
                              desfase.fila.categoria_actual)
                            : 'sin categoría'}
                        </strong>
                        .
                      </p>
                      {desfase.aMano ? (
                        <p className="text-text-secondary text-xs" data-testid="ficha-desfase-mano">
                          Está puesta a mano, así que el repaso no la toca salvo que lo marques.
                        </p>
                      ) : null}
                      <button
                        className="btn-outline text-xs"
                        data-testid="ficha-desfase-revisar"
                        onClick={() => setRevisarOpen(true)}
                      >
                        Revisar categorías
                      </button>
                    </div>
                  </Field>
                ) : null}
                <Field label="Posición">
                  {editing ? (
                    <select
                      className="input text-sm"
                      value={editData.position ?? ''}
                      onChange={(e) =>
                        setEditData((d) => ({
                          ...d,
                          position: (e.target.value || null) as ProfileFull['position'],
                        }))
                      }
                    >
                      <option value="">Sin posición</option>
                      {Object.entries(POSITION_LABELS).map(([v, l]) => (
                        <option key={v} value={v}>
                          {l}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span>{POSITION_LABELS[profile.position ?? ''] ?? '—'}</span>
                  )}
                </Field>
                <Field label="Menor de edad" icon={<Shield size={14} />}>
                  <span className={profile.is_minor ? 'text-warning' : 'text-text-secondary'}>
                    {profile.is_minor ? 'Sí' : 'No'}
                  </span>
                </Field>
              </div>

              {/* Campos deportivos */}
              <div className="border-t border-bg-border pt-4 space-y-3">
                <h4 className="text-text-secondary text-xs uppercase tracking-wider">
                  Datos deportivos
                </h4>
                <div className="grid grid-cols-3 gap-4">
                  <Field label="Club">
                    {editing ? (
                      <input
                        className="input text-sm"
                        value={editData.club ?? ''}
                        onChange={(e) => setEditData((d) => ({ ...d, club: e.target.value }))}
                        placeholder="Ej: Valencia CF"
                      />
                    ) : (
                      <span>{profile.club ?? '—'}</span>
                    )}
                  </Field>
                  <Field label="Equipo">
                    {editing ? (
                      <input
                        className="input text-sm"
                        value={editData.team ?? ''}
                        onChange={(e) => setEditData((d) => ({ ...d, team: e.target.value }))}
                        placeholder="Ej: Cadete A"
                      />
                    ) : (
                      <span>{profile.team ?? '—'}</span>
                    )}
                  </Field>
                  <Field label="División">
                    {editing ? (
                      <input
                        className="input text-sm"
                        value={editData.division ?? ''}
                        onChange={(e) => setEditData((d) => ({ ...d, division: e.target.value }))}
                        placeholder="Ej: Primera Regional"
                      />
                    ) : (
                      <span>{profile.division ?? '—'}</span>
                    )}
                  </Field>
                </div>
              </div>

              {/* LA FICHA MÉDICA. Se llamaba «Información de salud» aquí y de otras
                  dos formas en la app: tres nombres para una sola columna de
                  texto libre, y los placeholders empezando por «Alergias». De
                  ahí la impresión de que el campo era solo para eso (Jose,
                  probando el APK). Decisión del 2026-10-05: un solo rótulo.
                  El canónico vive en `app/src/lib/rotulosFichaMedica.ts`; aquí
                  va a mano porque los dos repos no comparten código. */}
              <div className="border-t border-bg-border pt-4 space-y-3">
                <h4 className="text-text-secondary text-xs uppercase tracking-wider flex items-center gap-2">
                  <HeartPulse size={13} className="text-error" /> Información médica
                </h4>
                {editing ? (
                  <textarea
                    data-testid="alumno-ficha-medica"
                    className="input text-sm w-full min-h-[80px]"
                    value={fichaBorrador}
                    onChange={(e) => setFichaBorrador(e.target.value)}
                    placeholder="Lo que el entrenador debe saber: alergias, enfermedades, lesiones, medicación…"
                    maxLength={1000}
                  />
                ) : (
                  <p className="text-sm text-white whitespace-pre-wrap">
                    {fichaEstado === 'cargando' ? (
                      <span className="text-text-secondary">Cargando…</span>
                    ) : fichaEstado === 'error' ? (
                      // ⚠️ Un fallo de lectura NO es una ficha vacía. Es la
                      // misma forma del #496 y de `lectura-fallida-no-es-ausencia`:
                      // el panel lee un fallo y lo pinta como ausencia.
                      <span className="text-error">
                        No se pudo leer la ficha. {fichaError ?? ''}
                      </span>
                    ) : fichaGuardada?.trim() ? (
                      fichaGuardada
                    ) : (
                      <span className="text-text-secondary">Sin información registrada.</span>
                    )}
                  </p>
                )}
              </div>

              {profile.is_minor && (
                <div className="border-t border-bg-border pt-4 space-y-3">
                  <h4 className="text-text-secondary text-xs uppercase tracking-wider">
                    Datos del tutor
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Nombre del tutor">
                      <span>{profile.parent_name ?? '—'}</span>
                    </Field>
                    <Field label="Email tutor">
                      {editing ? (
                        <input
                          className="input text-sm"
                          type="email"
                          data-testid="alumno-parent-email"
                          value={editData.parent_email ?? ''}
                          onChange={(e) =>
                            setEditData((d) => ({ ...d, parent_email: e.target.value }))
                          }
                        />
                      ) : (
                        <span>{profile.parent_email ?? '—'}</span>
                      )}
                      {/* El aviso solo cuando el valor CAMBIA. Puesto siempre
                          sería ruido de fondo y se dejaría de leer, que es
                          justo lo que no queremos de un aviso como este. */}
                      {editing &&
                        (editData.parent_email ?? '') !== (profile.parent_email ?? '') && (
                          <p
                            data-testid="alumno-parent-email-aviso"
                            className="text-warning mt-1 text-xs"
                          >
                            Este email decide en qué familia entra este alumno: cambiarlo lo mueve
                            de grupo. El vínculo con el tutor que ya tenga NO se rompe, pero una
                            conversión posterior agruparía a este alumno con el email nuevo.
                          </p>
                        )}
                    </Field>
                    <Field label="Teléfono tutor">
                      <span>{profile.parent_phone ?? '—'}</span>
                    </Field>
                    <Field label="Consentimiento parental">
                      <span className={profile.parental_consent ? 'text-success' : 'text-error'}>
                        {profile.parental_consent ? 'Firmado' : 'Pendiente'}
                      </span>
                    </Field>
                  </div>
                  {profile.parent_authorization_url && (
                    <a
                      href={profile.parent_authorization_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-ghost flex items-center gap-2 text-sm w-fit"
                    >
                      <FileText size={14} /> Ver documento de autorización
                    </a>
                  )}
                </div>
              )}

              {/* F4 · la familia como entidad. El bloque de arriba son las tres
                  columnas de texto de siempre, que siguen siendo el único dato
                  para quien todavía no tiene vínculo. */}
              <FamilyBlock studentId={profile.id} studentName={profile.full_name} />
            </div>

            {/* Estadísticas */}
            <div className="space-y-4">
              <div className="card">
                <h3 className="font-oswald text-sm uppercase tracking-wider text-white mb-3">
                  Estadísticas
                </h3>
                <div className="space-y-3">
                  <Stat label="Sesiones asistidas" value={totalSesionesAsistidas} />
                  <Stat label="Sesiones restantes" value={sesionesRestantes} gold />
                  <Stat label="Bonos activos" value={creditosActivos.length} />
                  <Stat label="Registro" value={format(new Date(profile.created_at), 'dd/MM/yy')} />
                </div>
              </div>

              <div
                className={`card border ${profile.active ? 'border-success/30' : 'border-error/30'}`}
              >
                <p className="text-text-secondary text-xs uppercase tracking-wider mb-1">
                  Estado de cuenta
                </p>
                <span className={profile.active ? 'badge-success' : 'badge-error'}>
                  {profile.active ? 'Activa' : 'Desactivada'}
                </span>
              </div>
            </div>
          </div>

          {/* Bonos con sesiones expandibles */}
          <div className="card" data-testid="section-credits">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-oswald text-sm uppercase tracking-wider text-white">Bonos</h3>
              <button
                onClick={() => {
                  setGiftError('');
                  setGiftForm({ sessions: 1, credit_type: 'grupal', reason: '', expiry_date: '' });
                  setGiftModal(true);
                }}
                className="btn-outline flex items-center gap-2 text-sm"
                data-testid="btn-grant-credits-open"
              >
                <Gift size={14} /> Regalar sesiones
              </button>
              <button
                onClick={() => setRegaloModal(true)}
                className="btn-outline flex items-center gap-2 text-sm"
                data-testid="btn-regalar-producto-open"
              >
                <Gift size={14} /> Regalar producto
              </button>
            </div>
            {credits.length === 0 ? (
              <p className="text-text-secondary text-sm">Sin bonos registrados</p>
            ) : (
              <div className="space-y-2">
                {credits.map((c) => {
                  const isExpanded = expandedCredit === c.id;
                  return (
                    <div
                      key={c.id}
                      className="border border-bg-border rounded-lg overflow-hidden"
                      data-testid={`credit-row-${c.id}`}
                    >
                      <div className="flex items-center gap-3 px-4 py-3 bg-bg-elevated">
                        <button
                          onClick={() => toggleCreditExpand(c.id)}
                          className="text-text-secondary hover:text-gold-primary transition-colors"
                        >
                          {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        </button>
                        <div className="flex-1 grid grid-cols-6 gap-2 items-center text-sm">
                          <span className="text-white font-medium col-span-2 flex items-center gap-2">
                            {c.is_gift && <Gift size={13} className="text-gold-primary" />}
                            {c.is_gift
                              ? c.gift_reason
                                ? `Regalo · ${c.gift_reason}`
                                : 'Bono regalo'
                              : (c.session_packs?.name ?? '—')}
                          </span>
                          <span
                            className={
                              CREDIT_TYPE_COLORS[c.credit_type ?? 'grupal'] ?? 'badge-gold'
                            }
                          >
                            {CREDIT_TYPE_LABELS[c.credit_type ?? 'grupal']}
                          </span>
                          <span className="text-text-secondary text-xs">
                            {c.sessions_used}/{c.sessions_total} usadas
                          </span>
                          <span
                            className={`font-oswald text-lg ${c.sessions_remaining <= 2 ? 'text-error' : 'text-gold-primary'}`}
                          >
                            {c.sessions_remaining} rest.
                          </span>
                          <div className="flex items-center gap-2">
                            <span className={c.active ? 'badge-success' : 'badge-error'}>
                              {c.active ? 'Activo' : 'Expirado'}
                            </span>
                            {c.expiry_date && (
                              <span className="text-text-secondary text-xs">
                                {format(new Date(c.expiry_date), 'dd/MM/yy')}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="px-4 py-3 bg-bg-surface border-t border-bg-border">
                          {loadingCredit === c.id ? (
                            <div className="flex items-center justify-center py-4">
                              <div className="w-5 h-5 border-2 border-gold-primary/30 border-t-gold-primary rounded-full animate-spin" />
                            </div>
                          ) : creditosIlegibles[c.id] ? (
                            <p className="text-error text-xs" data-testid={`bono-ilegible-${c.id}`}>
                              No se han podido leer las sesiones de este bono. No es que no se haya
                              usado: es que no se ha podido mirar.
                            </p>
                          ) : (creditEnrollments[c.id] ?? []).length === 0 ? (
                            <p className="text-text-secondary text-xs">
                              Sin sesiones registradas con este bono
                            </p>
                          ) : (
                            <div className="overflow-x-auto">
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="border-b border-bg-border">
                                    <th className="table-th">Fecha</th>
                                    <th className="table-th">Hora</th>
                                    <th className="table-th">Grupo / Clase</th>
                                    <th className="table-th">Asistencia</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(creditEnrollments[c.id] ?? []).map((e) => (
                                    <tr key={e.id} className="border-b border-bg-border/50">
                                      <td className="table-td">
                                        {e.sessions?.date
                                          ? format(new Date(e.sessions.date), 'dd/MM/yyyy')
                                          : '—'}
                                      </td>
                                      <td className="table-td text-text-secondary">
                                        {e.sessions?.time_start?.slice(0, 5) ?? '—'}
                                        {e.sessions?.time_end
                                          ? ` — ${e.sessions.time_end.slice(0, 5)}`
                                          : ''}
                                      </td>
                                      <td className="table-td text-text-secondary">
                                        {e.sessions?.special_classes?.name ??
                                          e.sessions?.groups?.name ??
                                          '—'}
                                      </td>
                                      <td className="table-td">
                                        {e.attendance === null ? (
                                          <span className="text-text-secondary">—</span>
                                        ) : e.attendance ? (
                                          <span className="badge-success">Asistió</span>
                                        ) : (
                                          <span className="badge-error">No asistió</span>
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Historial de sesiones */}
          <div className="card">
            <h3 className="font-oswald text-sm uppercase tracking-wider text-white mb-4">
              Historial de sesiones
            </h3>
            {enrollments.length === 0 ? (
              <p className="text-text-secondary text-sm">Sin inscripciones</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr>
                      <th className="table-th">Fecha</th>
                      <th className="table-th">Grupo</th>
                      <th className="table-th">Estado</th>
                      <th className="table-th">Asistencia</th>
                    </tr>
                  </thead>
                  <tbody data-testid="historial-sesiones">
                    {enrollments.map((e) => (
                      <tr key={e.id} className="table-row">
                        <td className="table-td text-xs">
                          {e.sessions?.date ? format(new Date(e.sessions.date), 'dd/MM/yyyy') : '—'}
                          <span className="text-text-secondary ml-2">{e.sessions?.time_start}</span>
                        </td>
                        <td className="table-td text-text-secondary">
                          {e.sessions?.special_classes?.name ?? e.sessions?.groups?.name ?? '—'}
                        </td>
                        <td className="table-td">
                          <span
                            className={
                              e.status === 'confirmed'
                                ? 'badge-success'
                                : e.status === 'waitlist'
                                  ? 'badge-warning'
                                  : 'badge-error'
                            }
                          >
                            {e.status === 'confirmed'
                              ? 'Confirmado'
                              : e.status === 'waitlist'
                                ? 'En espera'
                                : 'Cancelado'}
                          </span>
                        </td>
                        <td className="table-td">
                          {e.attendance === null ? (
                            <span className="text-text-secondary text-xs">—</span>
                          ) : e.attendance ? (
                            <span className="badge-success">Asistió</span>
                          ) : (
                            <span className="badge-error">No asistió</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Modal regalar sesiones */}
          {giftModal && (
            <div
              className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
              onClick={() => !granting && setGiftModal(false)}
              data-testid="modal-grant-credits"
            >
              <div
                className="bg-bg-surface border border-bg-border rounded-xl p-6 max-w-md w-full space-y-4"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center gap-2">
                  <Gift size={20} className="text-gold-primary" />
                  <h3 className="font-oswald text-lg text-white uppercase tracking-wider">
                    Regalar sesiones
                  </h3>
                </div>
                <p className="text-text-secondary text-sm">
                  Crea un bono regalo para{' '}
                  <strong className="text-white">{profile.full_name}</strong>. Se le notificará por
                  push y aparecerá en su perfil.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="label">Sesiones *</label>
                    <input
                      type="number"
                      min={1}
                      className="input"
                      value={giftForm.sessions}
                      onChange={(e) =>
                        setGiftForm((f) => ({ ...f, sessions: parseInt(e.target.value) || 1 }))
                      }
                      data-testid="input-grant-sessions"
                    />
                  </div>
                  <div>
                    <label className="label">Tipo *</label>
                    <select
                      className="input"
                      value={giftForm.credit_type}
                      onChange={(e) => setGiftForm((f) => ({ ...f, credit_type: e.target.value }))}
                      data-testid="select-grant-type"
                    >
                      <option value="grupal">Grupal</option>
                      <option value="individual">Individual</option>
                      <option value="especial">Especial</option>
                      {/* [254] PF ya no se ofrece aquí. Esta puerta concedía
                          Preparación Física sin autorización parental ninguna
                          mientras la nueva la exigía, y eso no lo había
                          decidido nadie: era el resultado de que esta función
                          naciera antes que el gate. El regalo de PF va por
                          "Regalar producto", que es la única que la sella. La
                          VENTA manual de PF sigue existiendo en la RPC y no se
                          toca (runbook §19); lo que se cierra es el regalo. */}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="label">Motivo</label>
                  <select
                    className="input mb-2"
                    value={giftForm.reason}
                    onChange={(e) => setGiftForm((f) => ({ ...f, reason: e.target.value }))}
                  >
                    <option value="">— Elegir motivo —</option>
                    <option value="Sesión de prueba">Sesión de prueba</option>
                    <option value="Primera clase cortesía">Primera clase cortesía</option>
                    <option value="Compensación">Compensación</option>
                    <option value="Promoción">Promoción</option>
                  </select>
                  <input
                    className="input"
                    value={
                      giftForm.reason.startsWith('Sesión') ||
                      giftForm.reason.startsWith('Primera') ||
                      giftForm.reason === 'Compensación' ||
                      giftForm.reason === 'Promoción'
                        ? ''
                        : giftForm.reason
                    }
                    onChange={(e) => setGiftForm((f) => ({ ...f, reason: e.target.value }))}
                    placeholder="O escribe un motivo personalizado..."
                  />
                </div>
                <div>
                  <label className="label">Caducidad (opcional)</label>
                  <input
                    type="date"
                    className="input"
                    value={giftForm.expiry_date}
                    onChange={(e) => setGiftForm((f) => ({ ...f, expiry_date: e.target.value }))}
                  />
                </div>
                {giftError && <p className="text-error text-sm">{giftError}</p>}
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => setGiftModal(false)}
                    disabled={granting}
                    className="btn-ghost"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleGrantCredits}
                    disabled={granting || giftForm.sessions < 1}
                    className="btn-gold flex items-center gap-2"
                    data-testid="btn-grant-submit"
                  >
                    <Gift size={14} /> {granting ? 'Creando...' : 'Regalar bono'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* [248] Lo que se le ha regalado, TODO, venga en forma de bono o no.
              La lista de bonos de arriba ya marca los regalados, pero no dice
              quién los dio; y de una matrícula, una suscripción o una camiseta
              regaladas no quedaba ni rastro en la ficha. Aquí sí, porque el
              pedido es el único sitio por el que pasan todos. */}
          {bloquesFallidos.includes(QUE_REGALOS) && (
            <div className="card" data-testid="regalos-ilegibles">
              <p className="text-error text-sm">
                No se ha podido leer lo que el club ya le ha regalado. Compruébalo antes de
                concederle nada: este bloque desaparecía entero, y eso se lee como «no se le ha
                regalado nunca».
              </p>
            </div>
          )}

          {regalos.length > 0 && (
            <div className="card" data-testid="bloque-regalos">
              <h3 className="font-oswald text-white uppercase tracking-wider mb-3 flex items-center gap-2">
                <Gift size={16} className="text-gold-primary" /> Regalos recibidos
              </h3>
              <div className="space-y-2">
                {regalos.map((r) => (
                  <div
                    key={r.id}
                    className="flex flex-wrap items-baseline gap-x-2 gap-y-1 border border-bg-border rounded-lg px-4 py-2 text-sm"
                    data-testid={`regalo-${r.id}`}
                  >
                    <span className="text-white font-medium">{r.que}</span>
                    <span className="text-text-secondary text-xs">
                      · {format(new Date(r.created_at), 'dd/MM/yy')}
                    </span>
                    <span className="text-text-secondary text-xs">
                      · de {(r.gifted_by && quienRegalo[r.gifted_by]) ?? 'alguien del club'}
                    </span>
                    {r.gift_reason && (
                      <span className="text-gold-primary text-xs">· {r.gift_reason}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {regaloModal && (
            <RegalarProductoModal
              studentId={id!}
              studentName={profile.full_name ?? 'este alumno'}
              // [254] Mismo criterio que la RPC: edad desconocida = menor.
              // `is_minor` es NOT NULL DEFAULT false, así que la ausencia de
              // dato se traduciría sola en "mayor" y se perdería el gate.
              esMenor={profile.is_minor !== false || profile.birth_date == null}
              nombreTutor={profile.parent_name?.trim() || null}
              onClose={() => setRegaloModal(false)}
              onHecho={() => loadAlumno(id!)}
            />
          )}

          {/* Evaluaciones — panel resumen. Vista completa en /evaluaciones.
          La 5.6b simplificó este panel a campos del schema real: antes leía
          overall_score/actitud/esfuerzo/motivacion/progress_note (modelo
          pre-033 que ya no existe) y mostraba "—" siempre. */}
          <div className="card">
            <h3 className="font-oswald text-sm uppercase tracking-wider text-white mb-4">
              Últimas evaluaciones
            </h3>
            {evaluations.length === 0 ? (
              <p className="text-text-secondary text-sm">Sin evaluaciones registradas</p>
            ) : (
              <div className="space-y-3">
                {evaluations.map((ev) => (
                  <div key={ev.id} className="card-elevated">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-text-secondary text-xs">
                        {format(new Date(`${ev.period_start}T00:00:00`), 'dd/MM/yyyy')}
                        {ev.period_end
                          ? ` → ${format(new Date(`${ev.period_end}T00:00:00`), 'dd/MM/yyyy')}`
                          : ''}{' '}
                        · Monitor: {ev.profiles?.full_name ?? '—'}
                      </p>
                    </div>
                    {ev.overall_comment && (
                      <p className="text-text-secondary text-xs italic">{ev.overall_comment}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* [AVISO] El repaso de categorías, el MISMO que abre la lista de
          alumnos. Va aquí, al nivel del contenedor, no dentro de ningún
          condicional: un modal que solo existe cuando otra cosa está abierta no
          se abre nunca. */}
      <RevisarCategoriasModal
        open={revisarOpen}
        onClose={() => setRevisarOpen(false)}
        onAplicado={async () => {
          if (!id) return;
          await loadAlumno(id);
          await mirarDesfase(id);
        }}
      />
    </div>
  );
}

function Field({
  label,
  icon,
  children,
}: {
  label: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-center gap-1 mb-1">
        {icon && <span className="text-text-secondary">{icon}</span>}
        <p className="label">{label}</p>
      </div>
      <div className="text-text-primary text-sm">{children}</div>
    </div>
  );
}

function Stat({ label, value, gold }: { label: string; value: string | number; gold?: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <p className="text-text-secondary text-xs">{label}</p>
      <p className={`font-oswald text-lg ${gold ? 'text-gold-primary' : 'text-white'}`}>{value}</p>
    </div>
  );
}
